import { useState } from 'react';
import { ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ahpraService } from '@/services/ahpraService';
import { useToast } from '@/providers/ToastProvider';
import type { AhpraRegistration } from '@/types/ahpra';
import { formatDate } from '@/lib/format';

interface Props {
  /** Pre-filled registration number, e.g. from a doctor's profile. */
  initialNumber?: string;
  /** Pre-filled verification result if you've already verified before. */
  initialResult?: AhpraRegistration | null;
  /** Called whenever verification completes successfully. */
  onVerified?: (reg: AhpraRegistration) => void;
  /** Show without a wrapper card (caller provides its own). */
  bare?: boolean;
}

/**
 * Single, reusable AHPRA live-verification panel. Used in:
 *   • Doctor registration (step 2)
 *   • Doctor profile settings (Practice tab)
 *
 * Calls the `ahpraService` which in Phase 2 invokes the
 * `verify-ahpra-registration` edge function (see supabase/functions/).
 */
export function AhpraVerifyPanel({ initialNumber = '', initialResult = null, onVerified, bare }: Props) {
  const toast = useToast();
  const [number, setNumber] = useState(initialNumber);
  const [result, setResult] = useState<AhpraRegistration | null>(initialResult);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const verify = async () => {
    setError(null);
    setResult(null);
    setVerifying(true);
    const res = await ahpraService.verify(number);
    setVerifying(false);
    if (!res.ok || !res.registration) {
      setError(res.error ?? 'Could not verify with the AHPRA register.');
      return;
    }
    setResult(res.registration);
    onVerified?.(res.registration);

    if (res.registration.status === 'registered' && !res.registration.hasConditions) {
      toast.success('AHPRA verified', `${res.registration.fullName} — registration in good standing.`);
    } else if (res.registration.hasConditions) {
      toast.warning('AHPRA verified — with conditions', 'Review the conditions on your registration.');
    } else {
      toast.error('Registration not in good standing', `Status: ${res.registration.status}`);
    }
  };

  const Wrapper = bare ? 'div' : 'div';
  return (
    <Wrapper className={bare ? 'space-y-3' : 'rounded-xl border border-border bg-bg/40 p-4 space-y-3'}>
      {!bare && (
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display font-semibold text-ink">Verify with AHPRA</p>
            <p className="text-xs text-ink-muted">
              Live check against the public AHPRA register. Updates your verified-doc status automatically.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        <Input
          className="flex-1"
          label="AHPRA registration number"
          placeholder="MED0000000000"
          value={number}
          onChange={(e) => { setNumber(e.target.value); setResult(null); setError(null); }}
        />
        <Button
          type="button"
          variant="outline"
          loading={verifying}
          onClick={verify}
          disabled={!number || verifying}
          iconLeft={verifying ? undefined : <ShieldCheck className="h-4 w-4" />}
        >
          {verifying ? 'Checking…' : 'Verify now'}
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-success/30 bg-success/5 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-display text-sm font-semibold text-ink inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-success" /> {result.fullName}
              </p>
              <p className="mt-0.5 text-xs text-ink-muted capitalize">
                {result.registrationType.replace('_', ' ')} registration
                {result.expiresOn ? ` · expires ${formatDate(result.expiresOn)}` : ''}
                {result.source === 'cached' ? ' · cached' : ''}
              </p>
            </div>
            <Badge tone={result.status === 'registered' ? 'success' : 'danger'} className="capitalize">
              {result.status}
            </Badge>
          </div>
          {result.specialties.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {result.specialties.map((s) => <Badge key={s} tone="primary">{s}</Badge>)}
            </div>
          )}
          {result.hasConditions && (
            <div className="mt-2 rounded-md border border-warning/30 bg-warning/5 p-2 text-xs text-ink">
              <p className="font-semibold text-warning">Conditions on this registration</p>
              <ul className="mt-1 list-disc pl-4 space-y-0.5 text-ink-muted">
                {result.conditions.map((c, i) => <li key={i}>{c.text}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </Wrapper>
  );
}
